import { useLocale, useTranslations } from "@acto/web-common/locales";
import { AnalyticsEvents, useAnalyticsSource, useTrack, type PlanOrigin } from "@acto/taskpane/core/analytics";
import { useCreateMeeting } from "@acto/taskpane/core/data-access/meetings/useCreateMeeting";
import { useCreateRecurringMeeting } from "@acto/taskpane/core/data-access/meetings/useCreateRecurringMeeting";
import { useCustomerById } from "@acto/taskpane/core/data-access/customers/useCustomerById";
import { toIsoDate } from "@acto/taskpane/field-sales-2/components/primitives/Calendar";
import { ChevronLeft } from "@acto/web-common/components";
import { FullScreenSheet } from "@acto/taskpane/field-sales-2/components/primitives/BottomSheet";
import { CtaRow, SolidCtaButton } from "@acto/taskpane/field-sales-2/components/primitives/SheetCta";
import { MeetingForm, type MeetingFormState } from "@acto/taskpane/field-sales-2/components/patterns/MeetingForm";
import { formatDayMonthLong } from "@acto/taskpane/field-sales-2/utils/format";

interface PlanMeetingContentProps {
  clientId: string;
  defaultDate?: Date;
  lockedDate?: Date;
  planOrigin?: PlanOrigin;
  onScheduled: () => void;
}

function PlanMeetingContent({ clientId, defaultDate, lockedDate, planOrigin, onScheduled }: PlanMeetingContentProps) {
  const t = useTranslations("fieldSalesTwo.planMeeting");
  const { locale } = useLocale();
  const analyticsSource = useAnalyticsSource();
  const { createMeeting, result } = useCreateMeeting();
  const { createRecurringMeeting, result: recurringResult } = useCreateRecurringMeeting();
  const customer = useCustomerById(clientId);
  const observationId = customer.data?.observation?.id;
  const track = useTrack();

  const handleAdd = async ({ date, times, repeat, cadence, isValid }: MeetingFormState) => {
    if (!isValid) return;

    const created = repeat
      ? await createRecurringMeeting({
          clientId,
          cadence,
          startDate: toIsoDate(date),
          scheduledStartTime: times.from,
          scheduledEndTime: times.to,
          observationId,
        })
      : await createMeeting({
          clientId,
          scheduledDate: toIsoDate(date),
          scheduledStartTime: times.from,
          scheduledEndTime: times.to,
          observationId,
        });

    if (created) {
      // FS2's sheet always creates (no update-existing branch), so hasExistingMeeting is always false.
      track(AnalyticsEvents.CUSTOMER_PLANNED, {
        customerId: clientId,
        scheduledDate: toIsoDate(date),
        hasExistingMeeting: false,
        isRecurring: repeat,
        cadence: repeat ? String(cadence) : undefined,
        source: analyticsSource,
        planOrigin,
      });
      onScheduled();
    }
  };

  return (
    <MeetingForm initialDate={defaultDate} fixedDate={lockedDate} clientId={clientId}>
      {(form) => (
        <CtaRow>
          <SolidCtaButton
            type="button"
            onClick={() => handleAdd(form)}
            disabled={result.fetching || recurringResult.fetching || customer.isPending || !form.isValid}
          >
            {t("addMeetingOn", { date: formatDayMonthLong(form.date, locale) })}
          </SolidCtaButton>
        </CtaRow>
      )}
    </MeetingForm>
  );
}

interface PlanMeetingSheetProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  clientId: string;
  /** Pre-selects the meeting day (e.g. the day chosen on the Calendar page). Defaults to today. */
  defaultDate?: Date;
  /** Fixes the meeting to this day and drops the day picker; `defaultDate` is then ignored. */
  lockedDate?: Date;
  /** Names the customer under the title. Only rendered alongside `lockedDate`. */
  clientName?: string;
  /** Only feeds the `AnalyticsEvents.CUSTOMER_PLANNED` payload. */
  planOrigin?: PlanOrigin;
}

export function PlanMeetingSheet({
  isOpen,
  onOpenChange,
  clientId,
  defaultDate,
  lockedDate,
  clientName,
  planOrigin,
}: PlanMeetingSheetProps) {
  const t = useTranslations("fieldSalesTwo.customerActions");
  const isDayFixed = Boolean(lockedDate);

  return (
    <FullScreenSheet
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      title={isDayFixed ? t("planMeeting.createTitle") : t("planMeeting.title")}
      subtitle={isDayFixed ? clientName : undefined}
      closeIcon={isDayFixed ? <ChevronLeft /> : undefined}
      closeAriaLabel={t("closeAriaLabel")}
    >
      <PlanMeetingContent
        clientId={clientId}
        defaultDate={defaultDate}
        lockedDate={lockedDate}
        planOrigin={planOrigin}
        onScheduled={() => onOpenChange(false)}
      />
    </FullScreenSheet>
  );
}
